import { useState, useEffect, useCallback } from 'react'
import { ChartSettings, ChartLibrary, DEFAULT_CHART_SETTINGS } from '../types/chartSettings'

const STORAGE_KEY = 'ioag-chart-settings'

/**
 * Hook for managing chart settings with localStorage persistence
 */
export function useChartSettings() {
  const [chartSettings, setChartSettings] = useState<ChartSettings>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored) {
        return JSON.parse(stored)
      }
    } catch (error) {
      console.error('Failed to load chart settings from localStorage:', error)
    }
    return DEFAULT_CHART_SETTINGS
  })

  // Persist to localStorage whenever settings change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(chartSettings))
    } catch (error) {
      console.error('Failed to save chart settings to localStorage:', error)
    }
  }, [chartSettings])

  const setChartLibrary = useCallback((library: ChartLibrary) => {
    setChartSettings((prev) => ({
      ...prev,
      library,
    }))
  }, [])

  const updateCanvasJSSettings = useCallback(
    (settings: Partial<ChartSettings['canvasjs']>) => {
      setChartSettings((prev) => ({
        ...prev,
        canvasjs: {
          ...prev.canvasjs,
          ...settings,
        },
      }))
    },
    []
  )

  const updateChartJSSettings = useCallback(
    (settings: Partial<ChartSettings['chartjs']>) => {
      setChartSettings((prev) => ({
        ...prev,
        chartjs: {
          ...prev.chartjs,
          ...settings,
        },
      }))
    },
    []
  )

  const resetChartSettings = useCallback(() => {
    setChartSettings(DEFAULT_CHART_SETTINGS)
  }, [])

  return {
    chartSettings,
    setChartLibrary,
    updateCanvasJSSettings,
    updateChartJSSettings,
    resetChartSettings,
  }
}
